import { createContext, PropsWithChildren, useEffect } from 'react'

import {
    BoltConnectionCreateDto,
    ProjectCreateDto,
    TreeNodeNewLocationDto,
} from '../../_generated/api/structure'
import DirectoryEntry from '../../models/DirectoryEntry'
import { useArchiveContext } from './ArchiveContext'
import DataContextValue from './DataContextValue'
import { useApiContext } from './ApiContext'
import { useUserContext } from './UserContext'
import { useProvideContext } from '../../hooks/context'
import { useToastContext } from './ToastContext'
import { useMutableObject } from '../../hooks/mutable'
import { useAction } from '../../hooks/action'
import { mapReadListResponse, throwIfResponseNotOk } from '../../tools/api'
import { applyStructureWriteResponse, StructureWriteResponse } from '../../tools/structure'
import Structure, { StructureTypes } from '../../models/Structure'

export interface StructureContextValue extends DataContextValue {
    structure: Structure
    structureLoading: boolean
    updateStructureByImpactDto: (dto: StructureWriteResponse) => void

    refreshStructure: () => Promise<void>
    moveStructure: (entry: DirectoryEntry, data: TreeNodeNewLocationDto) => Promise<void>
    duplicateStructure: (entry: DirectoryEntry) => Promise<void>
    restoreStructure: (entry: DirectoryEntry) => Promise<void>

    createProject: (data: ProjectCreateDto) => Promise<string | undefined>
    archiveProject: (projectId?: string) => Promise<boolean | undefined>
    restoreProject: (projectId?: string) => Promise<boolean | undefined>

    createBoltConnection: (data: BoltConnectionCreateDto) => Promise<string | undefined>
    archiveBoltConnection: (boltConnectionId?: string) => Promise<boolean | undefined>
    restoreBoltConnection: (boltConnectionId?: string) => Promise<boolean | undefined>
}

const StructureContext = createContext<StructureContextValue | undefined>(undefined)

export default function ({ children }: PropsWithChildren) {
    const { projectApi, boltConnectionApi, treeNodeApi } = useApiContext()
    const { includeArchived } = useArchiveContext()
    const { selectedOrganization, lazySelectedOrganizationId } = useUserContext()
    const Toast = useToastContext()

    const [loadedStructure, structureLoading, structureError, refreshStructure, replaceStructure] =
        useMutableObject(
            async () => {
                const orgId = selectedOrganization?.id
                if (!orgId) return null
                const response = await treeNodeApi.getAllTreesV3({
                    tenantId: orgId,
                    includeArchived,
                })
                return new Structure(mapReadListResponse(response, DirectoryEntry))
            },
            [selectedOrganization, includeArchived],
            true
        )

    const structure = loadedStructure ?? new Structure([])
    // Merge archived data into existing lists of unarchived data.
    // NOTE: This mechanism helps to limit performance and request overhead when toggling archive.

    const updateStructureByImpactDto = (dto: StructureWriteResponse) => {
        const updatedEntries = applyStructureWriteResponse(
            structure.map((entry) => entry),
            dto
        )
        replaceStructure(new Structure(updatedEntries))
    }

    const [moveLoading, moveError, moveStructure] = useAction(
        async (entry: DirectoryEntry, data: TreeNodeNewLocationDto) => {
            const id = entry.id
            const orgId = lazySelectedOrganizationId
            const response =
                entry.referenceType === StructureTypes.Project
                    ? await projectApi.moveProjectV3({
                          projectId: id,
                          tenantId: orgId,
                          treeNodeNewLocationDto: data,
                      })
                    : await boltConnectionApi.moveBoltConnectionV3({
                          boltConnectionId: id,
                          tenantId: orgId,
                          treeNodeNewLocationDto: data,
                      })
            throwIfResponseNotOk(response)
            updateStructureByImpactDto(response.data)
            Toast.completed('move', 'subject')
        }
    )

    const [duplicateLoading, duplicateError, duplicateStructure] = useAction(
        async (entry: DirectoryEntry) => {
            const id = entry.id
            const orgId = lazySelectedOrganizationId
            const response =
                entry.referenceType === StructureTypes.Project
                    ? await projectApi.duplicateProjectV3({ projectId: id, tenantId: orgId })
                    : await boltConnectionApi.duplicateBoltConnectionV3({
                          boltConnectionId: id,
                          tenantId: orgId,
                      })
            throwIfResponseNotOk(response)
            updateStructureByImpactDto(response.data)
            Toast.completed('duplicate', 'node')
        }
    )

    const [restoreLoading, restoreError, restoreStructure] = useAction(
        async (entry: DirectoryEntry) => {
            const id = entry.id
            const orgId = lazySelectedOrganizationId
            const response =
                entry.referenceType === StructureTypes.Project
                    ? await projectApi.restoreProjectV3({ projectId: id, tenantId: orgId })
                    : await boltConnectionApi.restoreBoltConnectionV3({
                          boltConnectionId: id,
                          tenantId: orgId,
                      })
            throwIfResponseNotOk(response)
            updateStructureByImpactDto(response.data)
            Toast.completed('restore', 'node')
        }
    )

    const [createLoading, createError, createProject] = useAction(
        async (data: ProjectCreateDto) => {
            const response = await projectApi.createProjectV3({
                tenantId: lazySelectedOrganizationId,
                projectCreateDto: data,
            })
            throwIfResponseNotOk(response)
            console.log(response.data)
            updateStructureByImpactDto(response.data)
            Toast.completed('create', 'project')
            return response.data.model.id ?? undefined
        }
    )

    const [archiveProjectLoading, archiveProjectError, archiveProject] = useAction(
        async (projectId?: string) => {
            if (!projectId) return
            const response = await projectApi.archiveProjectV3({
                projectId,
                tenantId: lazySelectedOrganizationId,
            })
            throwIfResponseNotOk(response)
            updateStructureByImpactDto(response.data)
            Toast.completed('archive', 'project')
            return true
        }
    )

    const [restoreProjectLoading, restoreProjectError, restoreProject] = useAction(
        async (projectId?: string) => {
            if (!projectId) return
            const response = await projectApi.restoreProjectV3({
                projectId,
                tenantId: lazySelectedOrganizationId,
            })
            throwIfResponseNotOk(response)
            updateStructureByImpactDto(response.data)
            Toast.completed('restore', 'project')
            return true
        }
    )

    const [createBoltConnectionLoading, createBoltConnectionError, createBoltConnection] =
        useAction(async (data: BoltConnectionCreateDto) => {
            const response = await boltConnectionApi.createBoltConnectionV3({
                tenantId: lazySelectedOrganizationId,
                boltConnectionCreateDto: data,
            })
            throwIfResponseNotOk(response)
            updateStructureByImpactDto(response.data)
            Toast.completed('create', 'hardware collection')
            return response.data.model.id ?? ''
        })

    const [archiveBoltConnectionLoading, archiveBoltConnectionError, archiveBoltConnection] =
        useAction(async (boltConnectionId?: string) => {
            if (!boltConnectionId) return
            const response = await boltConnectionApi.archiveBoltConnectionV3({
                boltConnectionId,
                tenantId: lazySelectedOrganizationId,
            })
            throwIfResponseNotOk(response)
            updateStructureByImpactDto(response.data)
            Toast.completed('archive', 'hardware collection')
            return true
        })

    const [restoreBoltConnectionLoading, restoreBoltConnectionError, restoreBoltConnection] =
        useAction(async (boltConnectionId?: string) => {
            if (!boltConnectionId) return
            const response = await boltConnectionApi.restoreBoltConnectionV3({
                boltConnectionId,
                tenantId: lazySelectedOrganizationId,
            })
            throwIfResponseNotOk(response)
            updateStructureByImpactDto(response.data)
            Toast.completed('restore', 'hardware collection')
            return true
        })

    // TODO: Currently, including all loading states in the initializing loading state, which is conceptually incorrect.
    //  The intermediate state SHOULD include all these, but the `loading` field is conceptually meant for initialization of the context, not procedural loading.
    const loading =
        structureLoading ||
        createLoading ||
        restoreLoading ||
        archiveProjectLoading ||
        restoreProjectLoading ||
        moveLoading ||
        duplicateLoading ||
        createBoltConnectionLoading ||
        archiveBoltConnectionLoading ||
        restoreBoltConnectionLoading
    const error =
        structureError ||
        createError ||
        restoreError ||
        archiveProjectError ||
        restoreProjectError ||
        moveError ||
        duplicateError ||
        createBoltConnectionError ||
        archiveBoltConnectionError ||
        restoreBoltConnectionError

    useEffect(() => {
        if (error) Toast.error(error, 'Structure error')
    }, [error])

    const value: StructureContextValue = {
        loading,
        error,

        structure,
        structureLoading,
        updateStructureByImpactDto,

        refreshStructure,
        moveStructure,
        duplicateStructure,
        restoreStructure,

        createProject,
        archiveProject,
        restoreProject,

        createBoltConnection,
        archiveBoltConnection,
        restoreBoltConnection,
    }

    return <StructureContext.Provider value={value}>{children}</StructureContext.Provider>
}

export const useStructureContext = () => useProvideContext(StructureContext, 'Structure')
